import { renderMergeText } from '../../lib/campaignMerge';
import type { TemplateSlots } from '../../types/campaign';

// Sample recipient used for preview merge substitution — must match
// packages/backend/src/routes/emailTemplates.ts SAMPLE_MERGE_VALUES so the
// preview matches what the server's own /email-templates/preview endpoint
// would render.
const SAMPLE_VALUES: Record<string, string> = {
  first_name: 'Jordan',
  last_name: 'Sample',
  team_name: 'Football (Varsity)',
  sport: 'Football',
  school_name: 'Sample High',
  school_city: 'Irvine',
  team_store_url: 'https://www.blastathletics.com/program_store_pages/1095',
  store_banner_url: 'https://blastathleticsdev.s3.amazonaws.com/uploads/picture/file/2335810/Tesoro_Football_Header1.png',
  campaign_image_url: 'https://blastathleticsdev.s3.amazonaws.com/uploads/picture/file/2335810/Tesoro_Football_Header1.png',
};

interface TemplatePreviewProps {
  subject: string;
  slots: TemplateSlots;
}

/**
 * Approximates the standard outbound email shape (preheader, subject,
 * image, headline, body, CTA button) using sample merge values, so a rep
 * or admin can see roughly what a real send will look like without
 * dispatching one. The body is rendered via `dangerouslySetInnerHTML` —
 * safe here because the HTML being rendered is the editor's own output
 * (the same whitelist the server enforces on save), not third-party markup.
 */
export function TemplatePreview({ subject, slots }: TemplatePreviewProps) {
  const preheader = renderMergeText(slots.preheader ?? '', SAMPLE_VALUES);
  const subjectResult = renderMergeText(subject, SAMPLE_VALUES);
  const headline = renderMergeText(slots.headline ?? '', SAMPLE_VALUES);
  const body = renderMergeText(slots.bodyHtml, SAMPLE_VALUES);
  const ctaLabel = renderMergeText(slots.ctaLabel ?? '', SAMPLE_VALUES);
  // Mirrors the backend's renderEmail, which merges slots.imageUrl the same
  // as every other slot — without this the preview's <img> would literally
  // request "{{store_banner_url}}" instead of resolving the sample image.
  const imageUrl = renderMergeText(slots.imageUrl ?? '', SAMPLE_VALUES);

  const missing = Array.from(
    new Set([
      ...preheader.missing,
      ...subjectResult.missing,
      ...headline.missing,
      ...body.missing,
      ...ctaLabel.missing,
      ...imageUrl.missing,
    ]),
  );

  return (
    <div className="card p-4 sm:p-5 space-y-3">
      <p className="text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-2 py-1.5">
        Sample data — real values are merged per recipient at send time.
      </p>
      {slots.preheader && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{preheader.text}</p>}
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
        Subject: <span className="text-gray-700 dark:text-gray-300">{subjectResult.text || '(no subject)'}</span>
      </p>
      {slots.imageUrl && (
        <img src={imageUrl.text} alt="" className="w-full rounded-md border border-gray-200 dark:border-gray-700" />
      )}
      {slots.headline && <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{headline.text}</h2>}
      <div
        className="review-doc text-sm text-gray-800 dark:text-gray-200 max-w-none"
        dangerouslySetInnerHTML={{ __html: body.text }}
      />
      {slots.ctaLabel && (
        <a
          href={slots.ctaUrl || '#'}
          onClick={(e) => e.preventDefault()}
          className="btn-primary inline-flex"
        >
          {ctaLabel.text}
        </a>
      )}
      {missing.length > 0 && (
        <p className="text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-2 py-1.5">
          Missing merge values: {missing.map((m) => `{{${m}}}`).join(', ')}
        </p>
      )}
    </div>
  );
}
